// Birim varyantı DR2 + DR12/3 — gösterge birimi için gruplu seçici ("Birim — müşteri genelinde").
// SearchSelect'in görsel dilini ve yaz-ara katlamasını (`foldSearch`) kullanır; SearchSelect gruplu
// liste taşımadığı ve birim listesi kısa olduğu için arama kutusu yerine APG "select-only combobox"
// kalıbı: odak düğmede kalır, seçenekler `aria-activedescendant` ile; ↑↓ / Home / End / Enter /
// Space / Esc / yaz-ara. Gruplar `role=group aria-label`. "Diğer birim…" görünür etiketli metin
// alanını açar (≤32, Enter/odak kaybında uygulanır) + kalıcı "çevrim ve uyum denetimi yapılmaz".
// Türetilmiş göstergede seçici yok: okunur "<birim> — girdilerden hesaplanır" + açılır şablon.
// Değer sözleşmesi: `value === null` = katalog varsayılanı (müşteri kaydı yok); varsayılan
// seçeneği `onChange(null)` döner.

import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from "react";
import { normalizeUnitKey } from "@mahir/shared";
import { foldSearch } from "../../lib/search-select";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import {
  OTHER_UNIT_VALUE,
  unitOptionsFor,
  type UnitOptionGroup,
  type UnitOptionGroupKey,
} from "../../lib/kpi-unit-options";
import { decisionText, helpText, inputStyle } from "./kpi-form-styles";

const C = KPI_UNIT_COPY;

export interface FlatUnitOption {
  index: number;
  groupKey: UnitOptionGroupKey;
  value: string;
  label: string;
  hint: string | null;
}

export function flattenUnitGroups(groups: readonly UnitOptionGroup[]): FlatUnitOption[] {
  const out: FlatUnitOption[] = [];
  for (const g of groups) {
    for (const o of g.options) out.push({ index: out.length, groupKey: g.key, ...o });
  }
  return out;
}

export type UnitPick = { kind: "default" } | { kind: "other" } | { kind: "unit"; unit: string };

export function resolveUnitPick(o: FlatUnitOption): UnitPick {
  if (o.groupKey === "default") return { kind: "default" };
  if (o.groupKey === "other" || o.value === OTHER_UNIT_VALUE) return { kind: "other" };
  return { kind: "unit", unit: o.value };
}

export interface UnitSelectState {
  open: boolean;
  active: number;
  typed: string;
  typedAt: number;
}
export const INITIAL_UNIT_SELECT_STATE: UnitSelectState = {
  open: false,
  active: 0,
  typed: "",
  typedAt: 0,
};
const TYPEAHEAD_MS = 600;

/** Saf klavye indirgeyicisi — `pick` = seçilen düz indeks (bileşen `resolveUnitPick` uygular). */
export function unitSelectKey(
  s: UnitSelectState,
  key: string,
  flat: readonly FlatUnitOption[],
  ctx: { selectedIndex: number; now?: number },
): { state: UnitSelectState; pick?: number; handled: boolean; stop?: boolean } {
  const last = Math.max(0, flat.length - 1);
  const start = ctx.selectedIndex >= 0 ? ctx.selectedIndex : 0;
  if (!s.open) {
    if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ") {
      return { state: { ...s, open: true, active: start }, handled: true };
    }
    if (key === "Home") return { state: { ...s, open: true, active: 0 }, handled: true };
    if (key === "End") return { state: { ...s, open: true, active: last }, handled: true };
  } else {
    if (key === "ArrowDown")
      return { state: { ...s, active: Math.min(last, s.active + 1) }, handled: true };
    if (key === "ArrowUp")
      return { state: { ...s, active: Math.max(0, s.active - 1) }, handled: true };
    if (key === "Home") return { state: { ...s, active: 0 }, handled: true };
    if (key === "End") return { state: { ...s, active: last }, handled: true };
    if (key === "Enter" || key === " ") {
      return { state: { ...s, open: false }, pick: s.active, handled: true };
    }
    // açık liste Esc'i tüketir — alttaki KpiModal'ın belge dinleyicisine sızıp modalı kapatmasın
    if (key === "Escape") return { state: { ...s, open: false }, handled: true, stop: true };
    if (key === "Tab") return { state: { ...s, open: false }, handled: false };
  }
  if (key.length === 1 && key !== " ") {
    const now = ctx.now ?? Date.now();
    const typed = (now - s.typedAt < TYPEAHEAD_MS ? s.typed : "") + key;
    const q = foldSearch(typed);
    const from = s.open ? s.active : start;
    // tek harf tekrarında bir sonrakine geç; çok harfte mevcuttan başla
    const offset = typed.length === 1 ? 1 : 0;
    for (let i = 0; i < flat.length; i++) {
      const idx = (from + offset + i) % flat.length;
      if (foldSearch(flat[idx]!.label).startsWith(q)) {
        return { state: { open: true, active: idx, typed, typedAt: now }, handled: true };
      }
    }
    return { state: { ...s, open: true, typed, typedAt: now }, handled: true };
  }
  return { state: s, handled: false };
}

interface KeyEventLike {
  key: string;
  preventDefault: () => void;
  stopPropagation: () => void;
}

/** Combobox düğmesinin onKeyDown yolu: indirgeyici + preventDefault + (açık listede Esc) yayılma kesimi. */
export function handleUnitSelectKeyDown(
  e: KeyEventLike,
  s: UnitSelectState,
  flat: readonly FlatUnitOption[],
  ctx: { selectedIndex: number; now?: number },
): ReturnType<typeof unitSelectKey> {
  const r = unitSelectKey(s, e.key, flat, ctx);
  if (r.handled) e.preventDefault();
  if (r.stop) e.stopPropagation();
  return r;
}

export function UnitListbox(props: {
  id: string;
  groups: readonly UnitOptionGroup[];
  activeIndex: number;
  selectedIndex: number;
  ariaLabel?: string;
  onPick: (index: number) => void;
  onHover: (index: number) => void;
}): JSX.Element {
  const { id, groups, activeIndex, selectedIndex, onPick, onHover } = props;
  let i = -1;
  return (
    <div id={id} role="listbox" aria-label={props.ariaLabel ?? C.fieldLabel} style={listStyle}>
      {groups.map((g) => (
        <div key={g.id ?? g.key} role="group" aria-label={g.label}>
          <div role="presentation" style={groupLabelStyle}>
            {g.label}
          </div>
          {g.options.map((o) => {
            i += 1;
            const idx = i;
            return (
              <div
                key={`${g.id ?? g.key}:${o.value}`}
                id={`${id}-${idx.toString()}`}
                role="option"
                aria-selected={idx === selectedIndex}
                onMouseEnter={() => onHover(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(idx)}
                style={{
                  ...optionStyle,
                  background:
                    idx === activeIndex
                      ? "color-mix(in srgb, var(--color-primary) 10%, var(--color-surface))"
                      : "transparent",
                  fontWeight: idx === selectedIndex ? 700 : 400,
                }}
              >
                <span>{o.label}</span>
                {o.hint ? <span style={hintStyle}>{o.hint}</span> : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export interface UnitSelectProps {
  id?: string;
  ariaLabel?: string;
  /** müşteri birimi; null = katalog varsayılanı */
  value: string | null;
  defaultUnit: string | null;
  variants: readonly string[];
  /** mevcut etkin birim (çevrilebilirlik + ipuçları buna göre) */
  current: string | null;
  /** tanımlıysa gösterge türetilmiş: seçici yerine okunur satır (null = birimsiz) */
  derivedUnit?: string | null;
  /** türetilmiş göstergenin birim şablonu (açılır ayrıntı) */
  derivedTemplate?: string | null;
  onChange: (unit: string | null) => void;
  disabled?: boolean;
}

export function UnitSelect(props: UnitSelectProps): JSX.Element {
  const { value, defaultUnit, variants, current, onChange, disabled } = props;
  const genId = useId();
  const listId = `${genId}-list`;
  const otherId = `${genId}-other`;
  const rootRef = useRef<HTMLDivElement>(null);
  const otherRef = useRef<HTMLInputElement>(null);
  const groups = useMemo(
    () => unitOptionsFor({ defaultUnit, variants, current }),
    [defaultUnit, variants, current],
  );
  const flat = useMemo(() => flattenUnitGroups(groups), [groups]);
  const selectedIndex = useMemo(() => {
    if (value === null) return 0;
    const k = normalizeUnitKey(value);
    const hit = flat.find((o) => o.groupKey !== "other" && normalizeUnitKey(o.value) === k);
    return hit ? hit.index : flat.length - 1; // listede yok → "Diğer birim…"
  }, [flat, value]);
  const isCustom = value !== null && flat[selectedIndex]?.groupKey === "other";
  const [state, setState] = useState<UnitSelectState>(INITIAL_UNIT_SELECT_STATE);
  const [otherOpen, setOtherOpen] = useState(false);
  const [draft, setDraft] = useState(isCustom ? (value ?? "") : "");
  const [focusOther, setFocusOther] = useState(false);

  useEffect(() => {
    if (isCustom && value !== null) setDraft(value);
  }, [isCustom, value]);

  useEffect(() => {
    if (!focusOther) return;
    otherRef.current?.focus();
    setFocusOther(false);
  }, [focusOther]);

  useEffect(() => {
    if (!state.open) return;
    document.getElementById(`${listId}-${state.active.toString()}`)?.scrollIntoView({
      block: "nearest",
    });
    function onDoc(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setState((s) => ({ ...s, open: false }));
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [state.open, state.active, listId]);

  if (props.derivedUnit !== undefined) {
    return (
      <div style={{ fontFamily: "var(--font-sans)" }}>
        <div style={decisionText}>{C.derivedReadonly(props.derivedUnit ?? C.unitless)}</div>
        {props.derivedTemplate ? (
          <details style={{ ...helpText, marginTop: 4 }}>
            <summary style={{ cursor: "pointer" }}>{C.derivedTemplate}</summary>
            <code>{props.derivedTemplate}</code>
          </details>
        ) : null}
      </div>
    );
  }

  function apply(index: number): void {
    const o = flat[index];
    if (!o) return;
    const p = resolveUnitPick(o);
    setState((s) => ({ ...s, open: false, active: index }));
    if (p.kind === "other") {
      setOtherOpen(true);
      setFocusOther(true);
      return;
    }
    setOtherOpen(false);
    onChange(p.kind === "default" ? null : p.unit);
  }

  function commitOther(): void {
    const u = draft.trim();
    if (u === "" || (value !== null && normalizeUnitKey(u) === normalizeUnitKey(value))) return;
    onChange(u);
  }

  const selected = flat[selectedIndex];
  const showOther = otherOpen || isCustom;
  const activeId = state.open ? `${listId}-${state.active.toString()}` : undefined;
  const ariaLabel = props.ariaLabel ?? C.fieldLabel;

  return (
    <div
      ref={rootRef}
      style={{ position: "relative", minWidth: 0, fontFamily: "var(--font-sans)" }}
    >
      <button
        type="button"
        id={props.id}
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={state.open}
        {...(state.open ? { "aria-controls": listId } : {})}
        {...(activeId ? { "aria-activedescendant": activeId } : {})}
        disabled={disabled}
        onClick={() =>
          setState((s) => ({ ...s, open: !s.open, active: selectedIndex >= 0 ? selectedIndex : 0 }))
        }
        onKeyDown={(e) => {
          const r = handleUnitSelectKeyDown(e, state, flat, { selectedIndex });
          setState(r.state);
          if (r.pick !== undefined) apply(r.pick);
        }}
        style={triggerStyle}
      >
        <span style={decisionText}>{isCustom ? value : (selected?.label ?? C.unitless)}</span>
        {!isCustom && selected?.hint ? <span style={hintStyle}>{selected.hint}</span> : null}
        <span aria-hidden="true" style={{ color: "var(--color-text-3)", marginLeft: "auto" }}>
          ▾
        </span>
      </button>
      {state.open ? (
        <div style={popStyle}>
          <UnitListbox
            id={listId}
            groups={groups}
            activeIndex={state.active}
            selectedIndex={selectedIndex}
            ariaLabel={ariaLabel}
            onPick={apply}
            onHover={(i) => setState((s) => ({ ...s, active: i }))}
          />
        </div>
      ) : null}
      {showOther ? (
        <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
          <label htmlFor={otherId} style={helpText}>
            {C.otherInputLabel}
          </label>
          <input
            ref={otherRef}
            id={otherId}
            type="text"
            maxLength={32}
            autoComplete="off"
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitOther}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitOther();
              }
            }}
            style={{ ...inputStyle, ...decisionText }}
          />
          <p style={{ ...helpText, margin: 0 }}>{C.otherNote}</p>
        </div>
      ) : null}
    </div>
  );
}

const triggerStyle: CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 8,
  textAlign: "left",
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  padding: "6px 10px",
  minHeight: 44,
  color: "var(--color-text)",
  cursor: "pointer",
  minWidth: 0,
};
const hintStyle: CSSProperties = {
  ...helpText,
  marginLeft: "auto",
  flexShrink: 0,
};
const popStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  top: "100%",
  marginTop: 4,
  zIndex: 60,
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  boxShadow: "0 10px 30px rgba(0,0,0,0.14)",
  padding: 6,
  minWidth: 260,
};
const listStyle: CSSProperties = { maxHeight: 320, overflowY: "auto" };
const groupLabelStyle: CSSProperties = {
  ...helpText,
  fontWeight: 600,
  padding: "8px 10px 4px",
};
const optionStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minHeight: 40,
  padding: "6px 10px",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: "1rem",
  color: "var(--color-text)",
};
